"use client";

import type { GroupProps } from "@chakra-ui/react/group";
import type { SlotRecipeProps } from "@chakra-ui/react/styled-system";
import { Avatar as ChakraAvatar } from "@chakra-ui/react/avatar";
import { Group } from "@chakra-ui/react/group";
import * as React from "react";
import { useAppSelector } from "redux/hooks";

type ImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

export interface AvatarProps extends ChakraAvatar.RootProps {
  name?: string;
  src?: string;
  srcSet?: string;
  loading?: ImageProps["loading"];
  icon?: React.ReactElement;
  fallback?: React.ReactNode;
  showInitials?: boolean;
}

const colorPalette = [
  "red",
  "blue",
  "green",
  "yellow",
  "purple",
  "orange",
  "cyan",
  "teal",
  "pink",
];

// Exported so text that identifies the same person (e.g. the group sender
// label) can share the avatar's hue — one derivation, no drift.
export const pickPalette = (name: string) => {
  const index = name.charCodeAt(0) % colorPalette.length;
  return colorPalette[index];
};

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  function Avatar(props, ref) {
    const {
      name,
      src,
      srcSet,
      loading,
      icon,
      fallback,
      children,
      showInitials = true,
      ...rest
    } = props;

    const { colorScheme } = useAppSelector((state) => state.theme);

    return (
      <ChakraAvatar.Root
        ref={ref}
        data-private={true}
        colorPalette={name ? pickPalette(name) : colorScheme}
        {...rest}
      >
        <AvatarFallback name={showInitials ? name : undefined} icon={icon}>
          {fallback}
        </AvatarFallback>
        <ChakraAvatar.Image
          src={src || undefined}
          srcSet={srcSet}
          loading={loading}
        />
        {children}
      </ChakraAvatar.Root>
    );
  }
);

interface AvatarFallbackProps extends ChakraAvatar.FallbackProps {
  name?: string;
  icon?: React.ReactElement;
}

const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
  function AvatarFallback(props, ref) {
    const { name, icon, children, ...rest } = props;
    return (
      <ChakraAvatar.Fallback ref={ref} {...rest}>
        {children}
        {name != null && children == null && <>{getInitials(name)}</>}
        {name == null && children == null && (
          <ChakraAvatar.Icon asChild={!!icon}>{icon}</ChakraAvatar.Icon>
        )}
      </ChakraAvatar.Fallback>
    );
  }
);

function getInitials(name: string): string {
  const names = name.trim().split(/\s+/);
  return names.length > 1
    ? `${names[0][0]}${names[names.length - 1][0]}`
    : names[0][0] || "";
}

interface AvatarGroupProps extends GroupProps, SlotRecipeProps<"avatar"> {}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  function AvatarGroup(props, ref) {
    const { size, variant, borderless, ...rest } = props;
    return (
      <ChakraAvatar.PropsProvider value={{ size, variant, borderless }}>
        <Group gap="0" spaceX="-3" ref={ref} {...rest} />
      </ChakraAvatar.PropsProvider>
    );
  }
);
